import { PersonName } from "@cur8/rich-entity";

/**
 * The member's name as it stands on record, with no preferred name.
 *
 * Views render this while the patient query is still in flight, so the
 * parts have to be joined rather than interpolated: a template literal
 * coerces the absent halves and puts the literal text "undefined undefined"
 * on screen instead of nothing.
 */
export function formatLegalName(name: PersonName | undefined): string {
  return [name?.firstName?.trim(), name?.lastName?.trim()]
    .filter(Boolean)
    .join(" ");
}

/**
 * Renders a member's name for display as `Firstname (Preferred) Surname`.
 *
 * The preferred name lives on `name.displayName`, written by the check-in
 * Preferred Name step. That step runs on body-scan and manual check-in;
 * non-body-scan follow-ups skip it (`IdentityCheckView` branches on
 * `isBodyScan`), so some members carry no displayName at all.
 *
 * It is rendered only where it differs from the legal name. That rule is
 * about the output, not a claim about the data: whatever puts a first name
 * or a full name into displayName, "Mikael (Mikael) Berggren" is noise
 * rather than information. How often records actually look like that has
 * not been measured.
 */
export function formatMemberName(name: PersonName | undefined): string {
  const firstName = name?.firstName?.trim() ?? "";
  const lastName = name?.lastName?.trim() ?? "";
  const legalName = formatLegalName(name);
  const preferredName = name?.displayName?.trim() ?? "";

  // Deliberately not suppressed when it matches the surname: a member who
  // goes by their surname is telling us something the row doesn't otherwise
  // show, and "Jonathan (Berg) Berg" reads better than greeting him Jonathan.
  const isRedundant =
    preferredName.length === 0 ||
    equalsIgnoringCase(preferredName, firstName) ||
    equalsIgnoringCase(preferredName, legalName);

  if (isRedundant) {
    return legalName;
  }

  // A member known only by a preferred name has nothing to wrap in
  // parentheses — show it plainly instead of "(miken)".
  if (legalName.length === 0) {
    return preferredName;
  }

  return [firstName, `(${preferredName})`, lastName].filter(Boolean).join(" ");
}

// toLowerCase, not toLocaleLowerCase: this compares two strings from the same
// record, so it must fold the same way on every device. The locale-aware form
// reads the device locale, and on a Turkish one "Ilse" and "ilse" stop
// matching (I lowercases to the dotless ı), so the same member would render
// with parentheses on one iPad and without them on the next.
function equalsIgnoringCase(a: string, b: string) {
  return b.length > 0 && a.toLowerCase() === b.toLowerCase();
}

/**
 * The name to address a member by in running copy ("Assign Ada to the skin
 * scanner"). Falls back through the first name to a neutral noun, because
 * displayName is optional on `PersonName` and these strings are template
 * literals — an absent name would otherwise print as "undefined".
 *
 * Only for copy that already addresses the member by their familiar name.
 * Views that deliberately show the legal name want `formatLegalName`.
 */
export function formatAddressableName(name: PersonName | undefined): string {
  return name?.displayName?.trim() || name?.firstName?.trim() || "the member";
}
